/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountStationInitialProperties,
  AccountStationResponseInterface,
  RequestAccountStationInterFace,
} from '../../interface/account-locations-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';
import accountv2Api from '../../utils/api/v2/accounts';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchAccountStation = createAsyncThunk<
  AccountStationResponseInterface,
  RequestAccountStationInterFace
>(
  'by-accountStation/fetchAccountStation',
  async (data: RequestAccountStationInterFace, { dispatch }) => {
    const { accountCustomId, ...query } = data;
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      const response = await accountApi.fetchStationListReport(
        accountCustomId,
        removeEmptyKeys(query),
      );
      return response.data.finalResult;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchAccountStationFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'by-accountStation/fetchAccountStationFilters',
  async (_, { dispatch }) => {
    try {
      const response = await accountv2Api.fetchAccountsListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountStationInitialProperties = {
  accountStation: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const AccountStationSlice = createSlice({
  name: 'account-station',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateAccountStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableAccountStationFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountStationInitialProperties>) => {
    builder
      .addCase(fetchAccountStation.pending, (state: AccountStationInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountStation.fulfilled,
        (
          state: AccountStationInitialProperties,
          action: PayloadAction<AccountStationResponseInterface>,
        ) => {
          state.loading = false;
          state.accountStation = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAccountStation.rejected, (state: AccountStationInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchAccountStationFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountStationFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchAccountStationFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateAccountStationFilters, disableAccountStationFilterApplied, setSortData } =
  AccountStationSlice.actions;
// Export the reducer
export default AccountStationSlice.reducer;
